/* eslint-disable max-classes-per-file */
import {
  printInShortDateFormat,
  printInShortDateAndHourFormat,
  printInHourDisplayFormat
} from '../libs/dateTimePrinting';
import {
  deserializeAndGetTypeYesplanDateAndOrTime,
  deserializeYesplanAPIDate,
  deserializeYesplanAPITime
} from '../libs/dateTimeUtilities';
import {
  formatAttachment,
  formatContactBooking,
  formatResourceBooking
} from '../libs/ypAPIDataFormatter';

/* eslint-disable no-use-before-define */

export class YPCustomDataObject {
  constructor(keyword, name, type, value, comment) {
    this.keyword = keyword;
    this.name = name;
    this.value = value;
    this.type = type;
    this.comment = comment;
  }

  stringForEmptyValue() {
    const noneTypes = ['Attachment', 'Labels', 'Integration info'];
    if (noneTypes.includes(this.getType())) return `(${i18n('none')})`;
    return `(${i18n('not filled out')})`;
  }

  getEmptyValue() {
    return this.hasEmptyValue() && this.stringForEmptyValue();
  }

  getType() {
    return this.type;
  }

  getTypeForRenderingAsReadOnly() {
    return 'string';
  }

  hasEmptyValue() {
    return this.value === null;
  }

  formatValue() {
    return this.hasEmptyValue() ? null : this.value;
  }
}

export class YPCustomDataNumber extends YPCustomDataObject {
  constructor(keyword, name, type, value, ispercentage, comment) {
    super(keyword, name, type, value, comment);
    this.ispercentage = ispercentage;
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    let stringValue = this.value.toString();
    if (this.ispercentage) stringValue += '%';
    return {
      ...super.formatValue(),
      string: stringValue
    };
  }
}

export class YPCustomDataText extends YPCustomDataObject {
  constructor(keyword, name, type, value, comment, multiline = false) {
    super(keyword, name, type, value, comment);
    this.multiline = multiline;
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return {
      multiline: this.multiline,
      string: this.value
    };
  }
}

export class YPCustomDataCheckbox extends YPCustomDataObject {
  constructor(keyword, name, type, value, options, comment) {
    const formattedOptions = options.map((option) => ({
      label: option,
      checked: value && value.findIndex((opt) => opt === option) > -1
    }));
    super(keyword, name, type, formattedOptions, comment);
    this.options = options;
    this.hasValue = value !== null;
  }

  getTypeForRenderingAsReadOnly() {
    return 'checkbox';
  }

  getEmptyValue() {
    return this.options.length === 0 && this.stringForEmptyValue();
  }

  hasEmptyValue() {
    return !this.hasValue;
  }

  formatValue() {
    return this.value;
  }
}

export class YPCustomDataDate extends YPCustomDataObject {
  constructor(keyword, name, type, value, comment, dateFormatSetting) {
    super(keyword, name, type, value, comment);
    if (value !== null) this.dateObject = deserializeYesplanAPIDate(value);
    this.dateFormatSetting = dateFormatSetting;
  }

  getType() {
    return this.type || 'Date';
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return {
      ...super.formatValue(),
      string: !this.hasEmptyValue()
        ? printInShortDateFormat(this.dateObject, this.dateFormatSetting)
        : ''
    };
  }
}

export class YPCustomDataTime extends YPCustomDataObject {
  constructor(keyword, name, type, value, comment, timeFormatSetting) {
    super(keyword, name, type, value, comment);
    if (value !== null) this.dateTimeObject = deserializeYesplanAPITime(value);
    this.timeFormatSetting = timeFormatSetting;
  }

  getType() {
    return this.type || 'Time';
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return {
      ...super.formatValue(),
      string: !this.hasEmptyValue()
        ? printInHourDisplayFormat(this.dateTimeObject, this.timeFormatSetting)
        : ''
    };
  }
}

export class YPCustomDataDateAndTime extends YPCustomDataObject {
  constructor(keyword, name, type, value, comment, dateFormatSetting, timeFormatSetting) {
    super(keyword, name, type, value, comment);
    if (value !== null) {
      const { isDate, isTime, dateObject } = deserializeAndGetTypeYesplanDateAndOrTime(value);
      this.isDate = isDate;
      this.isTime = isTime;
      this.dateAndOrTimeObject = dateObject;
    }
    this.dateFormatSetting = dateFormatSetting;
    this.timeFormatSetting = timeFormatSetting;
  }

  getType() {
    return this.type || 'DateAndTime';
  }

  // eslint-disable-next-line consistent-return
  printDateAndOrTime() {
    if (this.isDate && !this.isTime)
      return printInShortDateFormat(
        this.dateAndOrTimeObject,
        this.dateFormatSetting
      );
    if (this.isDate && this.isTime)
      return printInShortDateAndHourFormat(
        this.dateAndOrTimeObject,
        this.timeFormatSetting,
        this.dateFormatSetting,
        true,
        true,
        ', '
      );
    if (!this.isDate && this.isTime)
      return printInHourDisplayFormat(this.dateAndOrTimeObject, this.timeFormatSetting);
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return {
      ...super.formatValue(),
      string: this.value ? this.printDateAndOrTime() : ''
    };
  }
}

export class YPCustomDataContactBooking extends YPCustomDataObject {
  constructor(keyword, name, type, booking, comment) {
    super(keyword, name, type, booking, comment);
    this.booking = booking !== null ? formatContactBooking(booking) : null;
  }

  getTypeForRenderingAsReadOnly() {
    return 'contact';
  }

  setContactReference(contactReferences) {
    if (!this.hasEmptyValue()) {
      this.booking.setContactReference(contactReferences);
    }
  }

  isForContactLink() {
    return !this.hasEmptyValue() && this.booking.isForContactLink();
  }

  formatValue() {
    return this.booking;
  }
}

export class YPCustomDataResource extends YPCustomDataObject {
  constructor(keyword, name, type, resourceList, comment) {
    super(keyword, name, type, resourceList, comment);
    this.resource = resourceList ? formatResourceBooking(resourceList[0]) : null;
  }

  getTypeForRenderingAsReadOnly() {
    return 'resource';
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return this.resource;
  }
}

export class YPCustomDataAttachment extends YPCustomDataObject {
  constructor(keyword, name, type, attachment, comment) {
    super(keyword, name, type, attachment, comment);
    this.attachment = attachment !== null ? formatAttachment(attachment) : null;
  }

  getTypeForRenderingAsReadOnly() {
    return 'attachment';
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return this.attachment;
  }
}

export class YPCustomDataIntegrationField extends YPCustomDataObject {
  constructor(keyword, name, type, datatype, value, comment, dateTimeFormatSettings) {
    super(keyword, name, 'Integration info', value, comment);
    this.datatype = datatype;
    this.object =
      value !== null
        ? this.fromIntegrationDataTypeToCustomDataObject(dateTimeFormatSettings)
        : null;
  }

  getTypeForRenderingAsReadOnly() {
    return this.object === null
      ? super.getTypeForRenderingAsReadOnly()
      : this.object.getTypeForRenderingAsReadOnly();
  }

  hasEmptyValue() {
    return this.object === null;
  }

  formatValue() {
    if (this.hasEmptyValue()) return null;
    return this.object.formatValue();
  }

  fromIntegrationDataTypeToCustomDataObject(dateTimeFormatSettings) {
    const { value } = this;
    switch (this.datatype) {
      case 'Single-Line Text':
      case 'Single Option':
        return formatCustomDataObject({ type: 'String', value }, dateTimeFormatSettings);
      case 'Multi-Line Text':
        return formatCustomDataObject(
          { type: 'String', value, multiline: true },
          dateTimeFormatSettings
        );
      case 'Number':
        return formatCustomDataObject({ type: 'Number', value }, dateTimeFormatSettings);
      case 'Date':
        return formatCustomDataObject({ type: 'Date', value }, dateTimeFormatSettings);
      case 'Time':
        return formatCustomDataObject({ type: 'Time', value }, dateTimeFormatSettings);
      case 'Date and Time':
        return formatCustomDataObject(
          { type: 'DateAndTime', value },
          dateTimeFormatSettings
        );
      case 'Multiple Options':
        return formatCustomDataObject(
          {
            type: 'String',
            value: value ? value.join(', ') : null
          },
          dateTimeFormatSettings
        );
      default:
        throw new Error(
          `CustomDataElement: unknown custom data type for integration field: ${this.datatype}`
        );
    }
  }
}

export const formatCustomDataObject = (element, dateTimeFormatSettings) => {
  const { type, name, value, keyword } = element;
  const { dateFormatSetting, timeFormatSetting } = dateTimeFormatSettings;
  const comment = value ? value.comment : null;
  switch (type) {
    case 'Attachment':
      return new YPCustomDataAttachment(keyword, name, type, value, comment);
    case 'Contact':
      return new YPCustomDataContactBooking(
        keyword,
        name,
        'contactbooking',
        value,
        comment
      );
    case 'Checkbox':
      return new YPCustomDataCheckbox(
        keyword,
        name,
        type,
        value,
        element.options,
        comment
      );
    case 'Date':
      return new YPCustomDataDate(keyword, name, type, value, comment, dateFormatSetting);
    case 'DateAndTime':
      return new YPCustomDataDateAndTime(
        keyword,
        name,
        type,
        value,
        comment,
        dateFormatSetting,
        timeFormatSetting
      );
    case 'Integration info':
      return new YPCustomDataIntegrationField(
        keyword,
        name,
        type,
        element.datatype,
        value,
        comment,
        dateTimeFormatSettings
      );
    case 'Dropdown':
    case 'Label':
    case 'Publication Trigger':
    case 'Remote info':
    case 'Sequence':
    case 'String':
    case 'Y/N':
      return new YPCustomDataText(keyword, name, type, value, comment);
    case 'Labels':
      return new YPCustomDataText(
        keyword,
        name,
        type,
        value.length > 0 ? value.join(', ') : null,
        comment
      );
    case 'Number':
      return new YPCustomDataNumber(
        keyword,
        name,
        type,
        value,
        element.ispercentage,
        comment
      );
    case 'Resource':
      return new YPCustomDataResource(keyword, name, type, value, comment);
    case 'Text':
      return new YPCustomDataText(keyword, name, type, value, comment, true);
    case 'Time':
      return new YPCustomDataTime(keyword, name, type, value, comment, timeFormatSetting);
    case 'Mercurius Export Button':
    case 'Cevi Export Button':
    case 'Alfa Export Button':
      return null;
    default:
      throw new Error(`CustomDataElement: unknown custom data type: ${type}`);
  }
};
